import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, Building2, Shield, Users, Zap } from 'lucide-react'
import { Button, Badge } from '@/components/ui'
import { industries } from '@/data/industries'

export function Industries() {
  return (
    <section className="section-padding relative overflow-hidden">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <Badge className="mb-4">Industries We Serve</Badge>
          <h2 className="text-display-md md:text-display-lg font-heading font-bold text-white mb-4">
            Specialized Solutions for Financial Services
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Deep expertise in serving credit unions and banks with technology solutions designed for the unique needs of financial institutions.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {industries.map((industry, index) => (
            <motion.div
              key={industry.id}
              initial={{ opacity: 0, x: index === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="glass-card-hover rounded-3xl overflow-hidden group"
            >
              {/* Header */}
              <div className="p-8 pb-0">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-500 to-electric-500 flex items-center justify-center shadow-glow">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <Badge variant="secondary">{industry.shortTitle}</Badge>
                </div>
                <h3 className="text-2xl font-heading font-bold text-white mb-3 group-hover:text-accent-400 transition-colors">
                  {industry.title}
                </h3>
                <p className="text-slate-400 mb-6">
                  {industry.description}
                </p>
              </div>

              {/* Stats */}
              <div className="px-8 py-6 bg-white/5 grid grid-cols-2 gap-4">
                {industry.stats.slice(0, 4).map((stat) => (
                  <div key={stat.label}>
                    <div className="text-2xl font-heading font-bold text-accent-400">
                      {stat.value}
                    </div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="p-8 pt-6">
                <Link to={industry.href}>
                  <Button variant="secondary" className="w-full group/btn">
                    Learn More About Our {industry.shortTitle} Solutions
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Key benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {[
            { icon: Shield, label: 'Compliance Ready', desc: 'Meet NCUA, FFIEC, and other regulations' },
            { icon: Zap, label: 'Quick Deployment', desc: 'Get up and running in weeks, not months' },
            { icon: Users, label: 'Expert Support', desc: '24/7 dedicated technical support' },
            { icon: Building2, label: 'Core Integration', desc: 'Works with your existing systems' },
          ].map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              className="glass-card p-6 text-center"
            >
              <div className="w-12 h-12 mx-auto rounded-xl bg-accent-500/10 flex items-center justify-center mb-4">
                <item.icon className="w-6 h-6 text-accent-400" />
              </div>
              <h4 className="font-heading font-semibold text-white mb-2">{item.label}</h4>
              <p className="text-sm text-slate-400">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}


